//! Trade panel compatto in stile Polymarket. Sticky a destra del detail page.
//! 2 bottoni Sì/No (uno attivo solid + uno outline) → Importo + quick +$ → CTA "Trading".

import { useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Segmented } from "@/components/atoms/Segmented";
import { useMid } from "@/components/market/useMid";
import type { components } from "@/api/schema.gen";

type MarketView = components["schemas"]["MarketView"];

interface Props {
  market: MarketView;
  outcomeId: string;
}

type Side = "buy" | "sell";
type OrderMode = "market" | "limit";

const QUICK_AMOUNTS = [1, 5, 10, 100];

export function InlineTradePanel({ market, outcomeId }: Props) {
  const userId = useStore((s) => s.userId);
  const setWalletOpen = useStore((s) => s.setWalletOpen);
  const showToast = useStore((s) => s.showToast);
  const mid = useMid(market.slug, 3000);
  const outcome = market.outcomes.find((o) => o.id === outcomeId) ?? market.outcomes[0];

  const [side, setSide] = useState<Side>("buy");
  const [pick, setPick] = useState<"yes" | "no">("yes");
  const [mode, setMode] = useState<OrderMode>("market");
  const [amount, setAmount] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prezzo (in centesimi visibile, 0..1 backend)
  const yesPrice = mid;
  const noPrice = Math.max(0, Math.min(1, 1 - mid));
  const pickedPrice = pick === "yes" ? yesPrice : noPrice;

  // Per "buy": amount = $ spesi → shares = amount / price
  // Per "sell": amount = shares → $ ricavati = amount * price
  const shares = side === "buy" ? amount / Math.max(0.001, pickedPrice) : amount;
  const dollarValue = side === "buy" ? amount : amount * pickedPrice;
  const potentialPayout = side === "buy" ? shares : 0;

  const submit = async () => {
    if (!userId) {
      setWalletOpen(true);
      return;
    }
    if (amount <= 0 || shares <= 0) return;

    setSubmitting(true);
    setError(null);
    const priceStr = (Math.round(pickedPrice * 1000) / 1000).toFixed(3);
    const sizeStr = (Math.round(shares * 100) / 100).toFixed(2);
    // Mappa "Sì/No" → outcome del market in base alla pick.
    const outcomeForOrder = pick === "yes" ? market.outcomes[0] : market.outcomes[1] ?? outcome;
    const { data, error: err } = await api.POST("/v1/orders", {
      body: {
        market_id: market.id,
        outcome_id: outcomeForOrder.id,
        side,
        order_type: mode,
        time_in_force: "gtc",
        price: priceStr,
        size: sizeStr,
        idempotency_key: `inline-${market.id}-${Date.now()}`,
      },
    });
    setSubmitting(false);
    if (data) {
      showToast({
        kind: "success",
        message: `${side === "buy" ? "Buy" : "Sell"} ${sizeStr} ${outcomeForOrder.label} @ ${priceStr}`,
      });
      setAmount(0);
    } else if (err) {
      setError(
        typeof err === "object" && err && "message" in err
          ? String((err as { message: unknown }).message)
          : "Order rejected",
      );
    }
  };

  return (
    <div className="card flex w-full flex-col gap-4 p-4">
      {/* Mini market header */}
      <div className="flex items-start gap-3">
        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent" />
        <div className="min-w-0 flex-1">
          <div className="line-clamp-2 text-[13px] font-medium leading-tight">
            {market.title}
          </div>
          <div className="mt-1 text-[11px] text-muted">
            {outcome.label} · {pick === "yes" ? "Sì" : "No"}
          </div>
        </div>
      </div>

      {/* Compra/Vendi + Mode */}
      <div className="flex items-center justify-between">
        <Segmented<Side>
          options={[
            { value: "buy", label: "Compra" },
            { value: "sell", label: "Vendi" },
          ]}
          value={side}
          onChange={setSide}
        />
        <Segmented<OrderMode>
          options={[
            { value: "market", label: "Mercato" },
            { value: "limit", label: "Limite" },
          ]}
          value={mode}
          onChange={setMode}
        />
      </div>

      {/* Sì / No big buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className="flex items-center justify-center gap-2 rounded-[10px] px-4 py-3 text-[14px] font-semibold transition-colors"
          style={
            pick === "yes"
              ? {
                  background: "var(--yes)",
                  color: "#04140d",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25)",
                }
              : {
                  background: "var(--bg-3)",
                  color: "var(--text-2)",
                  border: "1px solid var(--line-strong)",
                }
          }
          onClick={() => setPick("yes")}
        >
          <span>Sì</span>
          <span className="font-mono">{(yesPrice * 100).toFixed(0)}¢</span>
        </button>
        <button
          type="button"
          className="flex items-center justify-center gap-2 rounded-[10px] px-4 py-3 text-[14px] font-semibold transition-colors"
          style={
            pick === "no"
              ? {
                  background: "var(--no)",
                  color: "#160808",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25)",
                }
              : {
                  background: "var(--bg-3)",
                  color: "var(--text-2)",
                  border: "1px solid var(--line-strong)",
                }
          }
          onClick={() => setPick("no")}
        >
          <span>No</span>
          <span className="font-mono">{(noPrice * 100).toFixed(0)}¢</span>
        </button>
      </div>

      {/* Importo */}
      <div className="flex items-center justify-between border-y border-line py-3">
        <span className="text-[12px] uppercase tracking-wider text-muted">
          Importo
        </span>
        <span className="font-mono text-[22px] font-semibold">
          ${amount.toFixed(2)}
        </span>
      </div>

      {/* Quick + amounts */}
      <div className="grid grid-cols-4 gap-2">
        {QUICK_AMOUNTS.map((v) => (
          <button
            key={v}
            type="button"
            className="rounded-[10px] border border-line bg-bg-2 py-2 text-[12px] text-text-2 transition-colors hover:bg-bg-3"
            onClick={() => setAmount((a) => +(a + v).toFixed(2))}
          >
            +${v}
          </button>
        ))}
      </div>

      {amount > 0 && side === "buy" && (
        <div className="rounded-[10px] border border-line bg-bg-2 p-3 text-[12px] text-text-2">
          <div className="flex justify-between">
            <span className="text-muted">Shares</span>
            <span className="font-mono">{shares.toFixed(2)}</span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-muted">Max payout</span>
            <span className="font-mono">${potentialPayout.toFixed(2)}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-[10px] border border-no/40 bg-no/10 px-3 py-2 text-[12px] text-no">
          {error}
        </div>
      )}

      <button
        type="button"
        className="rounded-[10px] py-3 text-[14px] font-semibold transition-colors"
        style={{
          background: amount > 0 ? "var(--accent)" : "var(--bg-3)",
          color: amount > 0 ? "#fff" : "var(--muted)",
          cursor: amount > 0 && !submitting ? "pointer" : "not-allowed",
        }}
        disabled={submitting || amount <= 0}
        onClick={submit}
      >
        {submitting ? "…" : userId ? "Trading" : "Connetti wallet"}
      </button>

      <p className="text-center text-[10.5px] text-muted">
        Facendo trading, accetti i{" "}
        <a href="#" className="underline hover:text-text-2">
          Termini di utilizzo
        </a>
        .
      </p>

      {amount > 0 && (
        <button
          type="button"
          className="text-center text-[10.5px] text-muted hover:text-text-2"
          onClick={() => setAmount(0)}
        >
          Reset
        </button>
      )}

      {/* Hide unused for now */}
      <span className="hidden">{dollarValue}</span>
    </div>
  );
}
